import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  RiCloseLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiCalendarLine,
  RiFileTextLine,
  RiUserLine,
  RiTeamLine,
  RiNewspaperLine,
  RiCalculatorLine,
  RiListCheck2,
  RiArrowDownSLine,
  RiBankLine,
  RiWalletLine,
  RiSaveLine,
  RiArrowLeftLine,
  RiEditLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = process.env.REACT_APP_API_URL;

const Spinner = ({ className = "w-4 h-4" }) => (
  <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function calcItemAmounts(item) {
  const sqcm = Number(item.width || 0) * Number(item.height || 0);
  const gross = sqcm * Number(item.rate || 0);
  const agencyComm = (gross * Number(item.agencyCommission || 0)) / 100;

  let otherAmount = 0;
  if (item.discountType === "discount") {
    otherAmount = -Number(item.otherDiscount || 0);
  } else {
    otherAmount = Number(item.otherPremium || 0);
  }

  const afterComm = gross - agencyComm + otherAmount;
  const cashDisc = (afterComm * Number(item.cashDiscount || 0)) / 100;
  const taxable = afterComm - cashDisc;
  const gstAmt = (taxable * Number(item.gst || 0)) / 100;
  const net = taxable + gstAmt;

  return {
    sqcm,
    gross,
    agencyComm,
    otherDiscount: item.discountType === "discount" ? item.otherDiscount : 0,
    otherPremium: item.discountType === "premium" ? item.otherPremium : 0,
    cashDisc,
    gstAmt,
    net,
  };
}

function calcTotals(items) {
  return items.reduce(
    (acc, item) => {
      const a = calcItemAmounts(item);
      acc.gross += a.gross;
      acc.agencyComm += a.agencyComm;
      acc.otherDiscount += a.otherDiscount;
      acc.otherPremium += a.otherPremium;
      acc.cashDisc += a.cashDisc;
      acc.gstAmt += a.gstAmt;
      acc.net += a.net;
      return acc;
    },
    {
      gross: 0,
      agencyComm: 0,
      otherDiscount: 0,
      otherPremium: 0,
      cashDisc: 0,
      gstAmt: 0,
      net: 0,
    },
  );
}

const Label = ({ children, className = "", required = false }) => (
  <label
    className={`text-[10px] font-semibold text-blue-600 uppercase tracking-widest mb-1 block ${className}`}
  >
    {children}
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const inputCls =
  "w-full bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition";

const selectCls =
  "w-full bg-[#3d2626] border border-[#3d2626] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition appearance-none cursor-pointer";

const SelectWrapper = ({ children }) => (
  <div className="relative">
    {children}
    <RiArrowDownSLine className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
  </div>
);

function ItemFinancials({ item }) {
  const amounts = calcItemAmounts(item);
  return (
    <div className="bg-blue-50/40 border border-blue-100 rounded-xl p-4 space-y-2.5 text-sm">
      <div className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-3">
        Financial Summary
      </div>

      <div className="flex items-center justify-between py-1.5 border-b border-blue-100">
        <span className="text-slate-500 text-xs">Gross Amount</span>
        <span className="font-bold text-slate-800 font-mono text-sm">
          ₹{fmt(amounts.gross)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs flex-1">Agency Commission</span>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min="0"
            max="100"
            value={item.agencyCommission}
            onChange={(e) => item._onChange("agencyCommission", e.target.value)}
            className="w-12 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
          />
          <span className="text-[10px] text-blue-400">%</span>
          <span className="font-semibold text-red-500 font-mono text-xs w-20 text-right">
            − ₹{fmt(amounts.agencyComm)}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs flex-1">Other</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="relative">
            <select
              value={item.discountType}
              onChange={(e) => item._onChange("discountType", e.target.value)}
              className="border border-blue-200 rounded-lg px-2 py-1 text-xs bg-white text-slate-700 focus:outline-none focus:border-blue-500 appearance-none pr-5 cursor-pointer"
            >
              <option value="discount">Discount</option>
              <option value="premium">Premium</option>
            </select>
            <RiArrowDownSLine className="absolute right-0.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
          </div>
          <input
            type="number"
            min="0"
            value={
              item.discountType === "discount"
                ? item.otherDiscount
                : item.otherPremium
            }
            onChange={(e) => {
              if (item.discountType === "discount")
                item._onChange("otherDiscount", e.target.value);
              else item._onChange("otherPremium", e.target.value);
            }}
            className="w-16 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
          />
          <span className="text-[10px] text-blue-400">Rs.</span>
          <span
            className={`font-semibold font-mono text-xs w-20 text-right ${item.discountType === "discount" ? "text-red-500" : "text-green-500"}`}
          >
            {item.discountType === "discount" ? "−" : "+"} ₹
            {fmt(
              item.discountType === "discount"
                ? amounts.otherDiscount
                : amounts.otherPremium,
            )}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs flex-1">Cash Discount</span>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min="0"
            max="100"
            value={item.cashDiscount}
            onChange={(e) => item._onChange("cashDiscount", e.target.value)}
            className="w-12 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
          />
          <span className="text-[10px] text-blue-400">%</span>
          <span className="font-semibold text-red-500 font-mono text-xs w-20 text-right">
            − ₹{fmt(amounts.cashDisc)}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs flex-1">GST</span>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min="0"
            max="100"
            value={item.gst}
            onChange={(e) => item._onChange("gst", e.target.value)}
            className="w-12 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
          />
          <span className="text-[10px] text-blue-400">%</span>
          <span className="font-semibold text-emerald-600 font-mono text-xs w-20 text-right">
            + ₹{fmt(amounts.gstAmt)}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2.5 border-t-2 border-blue-600/20 mt-1">
        <span className="text-blue-800 font-bold text-xs">Net Payable</span>
        <span className="font-extrabold text-blue-700 text-base font-mono">
          ₹{fmt(amounts.net)}
        </span>
      </div>
    </div>
  );
}

// ─── Map a raw RO item from API to local form item ────────────────────────────
const toFormItem = (apiItem) => ({
  id: apiItem._id || Math.random().toString(36).slice(2),
  _dbId: apiItem._id,
  media: apiItem.media?._id || apiItem.media || "",
  publication: apiItem.publication || "",
  edition: apiItem.edition || "",
  hue: apiItem.hue || "B/W",
  width: apiItem.width || "",
  height: apiItem.height || "",
  dop: apiItem.dop ? apiItem.dop.split("T")[0] : "",
  pagePosition: apiItem.pagePosition || "",
  adType: "", // will be resolved after ad-types load
  selectedAdName: apiItem.adType || "",
  rate: apiItem.ratePerSqCm || 0,
  roNumber: apiItem.roNumber || "",
  agencyCommission: apiItem.agencyCommission ?? 15,
  otherDiscount: apiItem.otherDiscount ?? 0,
  otherPremium: apiItem.otherPremium ?? 0,
  discountType: (apiItem.otherPremium || 0) > 0 ? "premium" : "discount",
  cashDiscount: apiItem.cashDiscount ?? 0,
  gst: apiItem.gst ?? 5,
  chequeDetails: {
    bankName: apiItem.chequeDetails?.bankName || "",
    chequeNo: apiItem.chequeDetails?.chequeNo || "",
    chequeDate: apiItem.chequeDetails?.chequeDate
      ? apiItem.chequeDetails.chequeDate.split("T")[0]
      : "",
  },
});

export default function EditReleaseOrder() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [media, setMedia] = useState([]);
  const [staff, setStaff] = useState([]);
  const [agents, setAgents] = useState([]);
  const [adTypes, setAdTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = (message, type = "success") => setToast({ message, type });

  // ── Fetch ad-types for a media id ────────────────────────────────────────
  const fetchAdTypesForMedia = async (mediaId) => {
    if (!mediaId || adTypes[mediaId]) return;
    try {
      const res = await axios.get(`${BASE_URL}/media/adtypes/${mediaId}`);
      if (res.data?.success && res.data?.adTypes) {
        setAdTypes((prev) => ({ ...prev, [mediaId]: res.data.adTypes }));
      }
    } catch {
      /* silently ignore */
    }
  };

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [roRes, clientsRes, mediaRes, staffRes, agentsRes] =
          await Promise.allSettled([
            axios.get(`${BASE_URL}/releaseOrder/${id}`),
            axios.get(`${BASE_URL}/bank/get-banks`),
            axios.get(`${BASE_URL}/media/get-all`),
            axios.get(`${BASE_URL}/admin/all-users`),
            axios.get(`${BASE_URL}/agent/all-agents`),
          ]);

        if (clientsRes.status === "fulfilled")
          setClients(
            clientsRes.value?.data?.data || clientsRes.value.data || [],
          );
        if (mediaRes.status === "fulfilled")
          setMedia(mediaRes.value?.data?.data || mediaRes.value.data || []);
        if (staffRes.status === "fulfilled") {
          const all = staffRes.value?.data?.data || staffRes.value.data || [];
          setStaff(all.filter((u) => u.userType === "staff"));
        }
        if (agentsRes.status === "fulfilled")
          setAgents(agentsRes.value?.data?.data || agentsRes.value.data || []);

        if (roRes.status === "fulfilled") {
          const ro = roRes.value?.data?.data || roRes.value.data;
          if (!ro) throw new Error("Release order not found");

          const formItems = (ro.items || []).map(toFormItem);

          setForm({
            roNumber: ro.roNumber || "",
            client: ro.client?._id || ro.client || "",
            caption: ro.caption || "",
            staffOrAgent: ro.agent ? "agent" : "staff",
            staffAgentId: ro.agent?._id || ro.staff?._id || "",
            date: ro.date ? ro.date.split("T")[0] : "",
            items: formItems,
          });

          // Prefetch ad-types for every media used in this RO
          const mediaIds = [
            ...new Set(formItems.map((i) => i.media).filter(Boolean)),
          ];
          await Promise.all(mediaIds.map(fetchAdTypesForMedia));
        } else {
          throw new Error("Failed to load release order");
        }
      } catch (err) {
        notify(err.message || "Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ─── After adTypes load, resolve the adType _id for each item ────────────
  useEffect(() => {
    if (!form) return;
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.adType || !item.media || !adTypes[item.media]) return item;
        const matched = adTypes[item.media]?.find((at) =>
          at.names?.includes(item.selectedAdName),
        );
        return matched ? { ...item, adType: matched._id } : item;
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adTypes]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const setFormField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const setItem = (id, key, val) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => (it.id === id ? { ...it, [key]: val } : it)),
    }));

  const setItemChequeDetail = (itemId, key, val) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) =>
        it.id === itemId
          ? { ...it, chequeDetails: { ...it.chequeDetails, [key]: val } }
          : it,
      ),
    }));

  const handleSATypeChange = (val) =>
    setForm((f) => ({ ...f, staffOrAgent: val, staffAgentId: "" }));

  const handleMediaChange = async (itemId, mediaId) => {
    const found = media.find((m) => m._id === mediaId);
    setItem(itemId, "media", mediaId);
    setItem(itemId, "adType", "");
    setItem(itemId, "selectedAdName", "");
    await fetchAdTypesForMedia(mediaId);
    if (found?.pricePerSqCm) setItem(itemId, "rate", found.pricePerSqCm);
    if (found?.organisation) setItem(itemId, "publication", found.organisation);
  };

  const handleAdTypeChange = (itemId, selectedAdTypeId) => {
    const currentItem = form.items.find((item) => item.id === itemId);
    const mediaId = currentItem?.media;
    const selectedAdType = adTypes[mediaId]?.find(
      (at) => at._id === selectedAdTypeId,
    );
    setItem(itemId, "adType", selectedAdTypeId);
    setItem(itemId, "selectedAdName", "");
    if (selectedAdType?.price) setItem(itemId, "rate", selectedAdType.price);
  };

  const handleAdNameSelect = (itemId, adName, selectedAdType) => {
    setItem(itemId, "selectedAdName", adName);
    if (selectedAdType?.price) setItem(itemId, "rate", selectedAdType.price);
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.client) return notify("Please select a client", "error");
    if (!form.staffAgentId)
      return notify("Please select a staff/agent", "error");
    if (form.items.length === 0) return notify("No items to save", "error");

    for (let i = 0; i < form.items.length; i++) {
      const item = form.items[i];
      if (!item.edition) {
        notify(`Please enter edition for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
      if (!item.selectedAdName) {
        notify(`Please select an ad name for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
      if (!item.width || item.width <= 0) {
        notify(`Valid width required for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
      if (!item.height || item.height <= 0) {
        notify(`Valid height required for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
      if (!item.rate || item.rate <= 0) {
        notify(`Valid rate required for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
    }

    setSubmitting(true);
    try {
      const processedItems = form.items.map(
        ({
          id: _localId,
          _dbId,
          _onChange,
          discountType,
          chequeDetails,
          ...rest
        }) => ({
          ...(_dbId ? { _id: _dbId } : {}),
          ...rest,
          width: parseFloat(rest.width) || 0,
          height: parseFloat(rest.height) || 0,
          ratePerSqCm: parseFloat(rest.rate) || 0,
          agencyCommission: parseFloat(rest.agencyCommission) || 0,
          otherDiscount:
            discountType === "discount"
              ? parseFloat(rest.otherDiscount) || 0
              : 0,
          otherPremium:
            discountType === "premium" ? parseFloat(rest.otherPremium) || 0 : 0,
          cashDiscount: parseFloat(rest.cashDiscount) || 0,
          gst: parseFloat(rest.gst) || 0,
          adType: rest.selectedAdName || rest.adType || "",
          chequeDetails: {
            bankName: chequeDetails?.bankName || "",
            chequeNo: chequeDetails?.chequeNo || "",
            chequeDate: chequeDetails?.chequeDate || null,
          },
        }),
      );

      const payload = {
        client: form.client,
        caption: form.caption,
        date: form.date,
        roNumber: form.roNumber,
        items: processedItems,
      };
      if (form.staffOrAgent === "staff") payload.staff = form.staffAgentId;
      else payload.agent = form.staffAgentId;

      await axios.put(`${BASE_URL}/releaseOrder/update/${id}`, payload);
      notify("Release Order updated successfully!");
      setTimeout(() => navigate(-1), 1200);
    } catch (err) {
      notify(
        err.response?.data?.message || "Failed to update Release Order",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-blue-400">
          <AiOutlineLoading3Quarters className="w-10 h-10 animate-spin text-blue-500" />
          <p className="text-sm font-medium text-slate-500">
            Loading release order…
          </p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 font-semibold mb-3">
            Release order not found.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline text-sm"
          >
            ← Go back
          </button>
        </div>
      </div>
    );
  }

  const saList = form.staffOrAgent === "staff" ? staff : agents;
  const totals = calcTotals(form.items);
  const activeItem = form.items[activeTab];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition"
            >
              <RiArrowLeftLine className="w-4 h-4" />
            </button>
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center text-white shadow-md shadow-amber-200">
              <RiEditLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Edit Release Order
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase font-mono">
                {form.roNumber}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 py-6 pb-16 w-full mx-auto space-y-5">
        {/* ── Order Details ── */}
        <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50">
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
              Order Details
            </span>
          </div>

          <div className="p-5 grid grid-cols-3 gap-5">
            {/* RO Number (read-only) */}
            <div>
              <Label>RO Number</Label>
              <input
                type="text"
                value={form.roNumber}
                onChange={(e) => setFormField("roNumber", e.target.value)}
                className={inputCls}
                placeholder="e.g. NG/001(A)/25-26"
              />
            </div>

            {/* Client */}
            <div>
              <Label>Client</Label>
              <SelectWrapper>
                <select
                  value={form.client}
                  onChange={(e) => setFormField("client", e.target.value)}
                  className={selectCls}
                >
                  <option value="">--Select--</option>
                  {clients.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.bankName || c.name}
                    </option>
                  ))}
                </select>
              </SelectWrapper>
            </div>

            {/* Date */}
            <div>
              <Label>Date</Label>
              <div className="relative">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setFormField("date", e.target.value)}
                  className={`${inputCls} pl-9`}
                />
              </div>
            </div>

            {/* Staff / Agent */}
            <div>
              <Label>Staff / Agent</Label>
              <div className="flex gap-2 mb-2">
                {["staff", "agent"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleSATypeChange(type)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold capitalize border transition
                      ${
                        form.staffOrAgent === type
                          ? "bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-200"
                          : "bg-blue-50 border-blue-200 text-blue-500 hover:bg-blue-100"
                      }`}
                  >
                    {type === "staff" ? (
                      <RiUserLine className="inline w-3.5 h-3.5 mr-1" />
                    ) : (
                      <RiTeamLine className="inline w-3.5 h-3.5 mr-1" />
                    )}
                    {type}
                  </button>
                ))}
              </div>
              <SelectWrapper>
                <select
                  value={form.staffAgentId}
                  onChange={(e) => setFormField("staffAgentId", e.target.value)}
                  className={selectCls}
                >
                  <option value="">--Select--</option>
                  {saList.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </SelectWrapper>
            </div>

            {/* Caption */}
            <div className="col-span-2">
              <Label>Caption</Label>
              <input
                type="text"
                value={form.caption}
                onChange={(e) => setFormField("caption", e.target.value)}
                className={inputCls}
                placeholder="Ad caption / description"
              />
            </div>
          </div>
        </div>

        {/* ── Publication Items ── */}
        <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest flex items-center gap-2">
              <RiNewspaperLine className="w-4 h-4 text-blue-500" />
              Publication Items
            </span>
          </div>

          {form.items.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <RiNewspaperLine className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No items found in this release order.</p>
            </div>
          ) : (
            <>
              {/* Tab bar */}
              <div className="flex items-center gap-1 px-5 pt-4 overflow-x-auto pb-0 border-b border-blue-100">
                {form.items.map((item, idx) => {
                  const isActive = activeTab === idx;
                  return (
                    <div
                      key={item.id}
                      onClick={() => setActiveTab(idx)}
                      className={`group relative flex items-center gap-1.5 px-4 py-2 rounded-t-xl text-xs font-semibold cursor-pointer border-b-2 transition-all whitespace-nowrap select-none
                        ${
                          isActive
                            ? "bg-white border-amber-500 text-amber-700 shadow-sm"
                            : "bg-blue-50/60 border-transparent text-blue-400 hover:text-blue-600 hover:bg-blue-50"
                        }`}
                    >
                      <RiNewspaperLine className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Item {idx + 1}</span>
                    </div>
                  );
                })}
              </div>

              {/* Active item editor */}
              {activeItem &&
                (() => {
                  const itemWithCallback = {
                    ...activeItem,
                    _onChange: (key, val) => setItem(activeItem.id, key, val),
                  };
                  const sqcm =
                    Number(activeItem.width || 0) *
                    Number(activeItem.height || 0);

                  return (
                    <div className="p-5">
                      <div className="grid grid-cols-3 gap-5">
                        {/* Left/centre: all fields */}
                        <div className="col-span-2 grid grid-cols-2 gap-4">
                          {/* RO Number per item (read-only hint) */}
                          <div>
                            <Label>RO Number</Label>
                            <input
                              type="text"
                              value={activeItem.roNumber}
                              onChange={(e) =>
                                setItem(
                                  activeItem.id,
                                  "roNumber",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                              placeholder="e.g. NG/001(A)/25-26"
                            />
                          </div>

                          {/* Media */}
                          <div>
                            <Label>Media</Label>
                            <SelectWrapper>
                              <select
                                value={activeItem.media}
                                onChange={(e) =>
                                  handleMediaChange(
                                    activeItem.id,
                                    e.target.value,
                                  )
                                }
                                className={selectCls}
                              >
                                <option value="">--Select--</option>
                                {media.map((m) => (
                                  <option key={m._id} value={m._id}>
                                    {m.organisation}
                                  </option>
                                ))}
                              </select>
                            </SelectWrapper>
                          </div>

                          {/* Publication */}
                          <div>
                            <Label>Publication</Label>
                            <input
                              value={activeItem.publication}
                              onChange={(e) =>
                                setItem(
                                  activeItem.id,
                                  "publication",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                              placeholder="Publication name"
                            />
                          </div>

                          {/* Edition */}
                          <div>
                            <Label required>Edition</Label>
                            <input
                              value={activeItem.edition}
                              onChange={(e) =>
                                setItem(
                                  activeItem.id,
                                  "edition",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                              placeholder="Edition (required)"
                            />
                          </div>

                          {/* HUE */}
                          <div>
                            <Label>HUE</Label>
                            <SelectWrapper>
                              <select
                                value={activeItem.hue}
                                onChange={(e) =>
                                  setItem(activeItem.id, "hue", e.target.value)
                                }
                                className={selectCls}
                              >
                                <option>B/W</option>
                                <option>Color</option>
                              </select>
                            </SelectWrapper>
                          </div>

                          {/* Width */}
                          <div>
                            <Label>Width (cm)</Label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={activeItem.width}
                              onChange={(e) =>
                                setItem(activeItem.id, "width", e.target.value)
                              }
                              className={inputCls}
                              placeholder="0"
                            />
                          </div>

                          {/* Height */}
                          <div>
                            <Label>Height (cm)</Label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={activeItem.height}
                              onChange={(e) =>
                                setItem(activeItem.id, "height", e.target.value)
                              }
                              className={inputCls}
                              placeholder="0"
                            />
                          </div>

                          {/* DOP */}
                          <div>
                            <Label>Date of Publication</Label>
                            <div className="relative">
                              <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                              <input
                                type="date"
                                value={activeItem.dop}
                                onChange={(e) =>
                                  setItem(activeItem.id, "dop", e.target.value)
                                }
                                className={`${inputCls} pl-9`}
                              />
                            </div>
                          </div>

                          {/* Page/Position */}
                          <div>
                            <Label>Page / Position</Label>
                            <input
                              value={activeItem.pagePosition}
                              onChange={(e) =>
                                setItem(
                                  activeItem.id,
                                  "pagePosition",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                              placeholder="Page / Position"
                            />
                          </div>

                          {/* Ad Type */}
                          <div>
                            <Label>Ad Type</Label>
                            <SelectWrapper>
                              <select
                                value={activeItem.adType}
                                onChange={(e) =>
                                  handleAdTypeChange(
                                    activeItem.id,
                                    e.target.value,
                                  )
                                }
                                className={selectCls}
                                disabled={!activeItem.media}
                              >
                                <option value="">--Select Ad Type--</option>
                                {(adTypes[activeItem.media] || []).map(
                                  (adType) => (
                                    <option key={adType._id} value={adType._id}>
                                      Type {adType.typeNumber} - ₹{adType.price}
                                      /sq cm ({adType.names?.length || 0} names)
                                    </option>
                                  ),
                                )}
                              </select>
                            </SelectWrapper>

                            {/* Ad name chips */}
                            {activeItem.adType && adTypes[activeItem.media] && (
                              <div className="mt-3">
                                <div className="text-[10px] font-semibold text-blue-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                                  <RiListCheck2 className="w-3.5 h-3.5" />
                                  Available Ad Names for Type{" "}
                                  {
                                    adTypes[activeItem.media].find(
                                      (at) => at._id === activeItem.adType,
                                    )?.typeNumber
                                  }
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {adTypes[activeItem.media]
                                    .find((at) => at._id === activeItem.adType)
                                    ?.names?.map((name, idx) => {
                                      const selectedAdType = adTypes[
                                        activeItem.media
                                      ].find(
                                        (at) => at._id === activeItem.adType,
                                      );
                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          onClick={() =>
                                            handleAdNameSelect(
                                              activeItem.id,
                                              name,
                                              selectedAdType,
                                            )
                                          }
                                          className={`text-xs px-3 py-1.5 rounded-lg transition ${
                                            activeItem.selectedAdName === name
                                              ? "bg-amber-500 text-white border border-amber-500 shadow-sm"
                                              : "bg-white border border-blue-200 text-slate-700 hover:bg-blue-50"
                                          }`}
                                        >
                                          {name}
                                        </button>
                                      );
                                    })}
                                </div>
                                {activeItem.selectedAdName && (
                                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-semibold text-amber-700">
                                        Selected: {activeItem.selectedAdName}
                                      </span>
                                      <span className="text-sm font-bold text-amber-700">
                                        Rate: ₹{activeItem.rate}/sq cm
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Fallback: show selected ad name as text if adType not yet resolved */}
                            {!activeItem.adType &&
                              activeItem.selectedAdName && (
                                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                  <span className="text-xs font-semibold text-amber-700">
                                    Current: {activeItem.selectedAdName}
                                  </span>
                                </div>
                              )}
                          </div>

                          {/* Rate */}
                          <div>
                            <Label>Rate (Rs/Sq.cm)</Label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={activeItem.rate}
                              onChange={(e) =>
                                setItem(activeItem.id, "rate", e.target.value)
                              }
                              className={inputCls}
                              placeholder="0"
                            />
                          </div>

                          {/* Area / Line Gross */}
                          <div className="col-span-2">
                            <div className="flex items-center gap-4 px-4 py-2.5 bg-blue-50/60 border border-blue-100 rounded-xl">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">
                                  Area
                                </span>
                                <span className="font-mono font-bold text-slate-700 text-sm">
                                  {sqcm.toFixed(2)} sq.cm
                                </span>
                              </div>
                              <div className="w-px h-5 bg-blue-200" />
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">
                                  Line Gross
                                </span>
                                <span className="font-mono font-bold text-blue-700 text-sm">
                                  ₹{fmt(sqcm * Number(activeItem.rate || 0))}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Cheque Details */}
                          <div className="col-span-2 border-t border-blue-100 pt-4 mt-2">
                            <div className="flex items-center gap-2 mb-3">
                              <RiBankLine className="w-4 h-4 text-blue-500" />
                              <Label className="!mb-0">Cheque Details</Label>
                              <span className="text-[10px] text-blue-400 ml-2">
                                Item {activeTab + 1}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <Label>Bank Name</Label>
                                <div className="relative">
                                  <RiBankLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                                  <input
                                    type="text"
                                    value={
                                      activeItem.chequeDetails?.bankName || ""
                                    }
                                    onChange={(e) =>
                                      setItemChequeDetail(
                                        activeItem.id,
                                        "bankName",
                                        e.target.value,
                                      )
                                    }
                                    className={`${inputCls} pl-9`}
                                    placeholder="e.g., HDFC Bank"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Cheque No.</Label>
                                <div className="relative">
                                  <RiWalletLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                                  <input
                                    type="text"
                                    value={
                                      activeItem.chequeDetails?.chequeNo || ""
                                    }
                                    onChange={(e) =>
                                      setItemChequeDetail(
                                        activeItem.id,
                                        "chequeNo",
                                        e.target.value,
                                      )
                                    }
                                    className={`${inputCls} pl-9`}
                                    placeholder="e.g., 123456"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Cheque Date</Label>
                                <div className="relative">
                                  <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                                  <input
                                    type="date"
                                    value={
                                      activeItem.chequeDetails?.chequeDate || ""
                                    }
                                    onChange={(e) =>
                                      setItemChequeDetail(
                                        activeItem.id,
                                        "chequeDate",
                                        e.target.value,
                                      )
                                    }
                                    className={`${inputCls} pl-9`}
                                  />
                                </div>
                              </div>
                            </div>
                            <p className="text-[10px] text-blue-400 mt-2 italic">
                              These fields are optional and can be left blank
                            </p>
                          </div>
                        </div>

                        {/* Right: financials */}
                        <div className="col-span-1">
                          <ItemFinancials item={itemWithCallback} />
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </>
          )}
        </div>

        {/* ── Grand Total (if multiple items) ── */}
        {form.items.length > 1 && (
          <div className="flex justify-end">
            <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden w-full max-w-md">
              <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50">
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest flex items-center gap-2">
                  <RiCalculatorLine className="w-4 h-4 text-blue-500" />
                  Grand Total — All Items
                </span>
              </div>
              <div className="p-5 space-y-2 text-sm">
                <div className="flex justify-between py-1.5 border-b border-blue-50">
                  <span className="text-slate-600 font-medium">
                    Total Gross
                  </span>
                  <span className="font-bold text-slate-800 font-mono">
                    ₹{fmt(totals.gross)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Agency Commission</span>
                  <span className="text-red-500 font-mono">
                    − ₹{fmt(totals.agencyComm)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Other Discount</span>
                  <span className="text-red-500 font-mono">
                    − ₹{fmt(totals.otherDiscount)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Other Premium</span>
                  <span className="text-green-500 font-mono">
                    + ₹{fmt(totals.otherPremium)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Cash Discount</span>
                  <span className="text-red-500 font-mono">
                    − ₹{fmt(totals.cashDisc)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>GST</span>
                  <span className="text-emerald-600 font-mono">
                    + ₹{fmt(totals.gstAmt)}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t-2 border-blue-600/20 mt-1">
                  <span className="text-blue-800 font-bold text-sm">
                    Net Payable Amount
                  </span>
                  <span className="font-extrabold text-blue-700 text-lg font-mono">
                    ₹{fmt(totals.net)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Action buttons ── */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 font-semibold transition"
          >
            <RiCloseLine className="w-4 h-4" /> Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting || form.items.length === 0}
            className="flex items-center gap-2 px-7 py-2.5 rounded-xl text-sm font-bold bg-amber-500 hover:bg-amber-600 text-white transition shadow-lg shadow-amber-200 disabled:opacity-60"
          >
            {submitting ? <Spinner /> : <RiSaveLine className="w-4 h-4" />}
            {submitting ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
